import type React from 'react';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type { DepartmentId } from '@vlabs/api/artemy/model';
import Loader from '@vlabs/shared/legacy-components/loader/Loader';
import type { WithDataTestid } from '@vlabs/uikit/types';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';
import { Tree, TreeSwitcherIcon } from '@vlabs/monokit/shared/ui/tree';
import type { TreeNodeProps } from '@vlabs/monokit/shared/ui/tree';

import st from './DepartmentsTree.module.sass';
import { MAX_DEPARTMENT_LEVEL, useDepartmentTree } from '../model/useDepartmentTree';
import type { DepartmentTreeDataNode } from '../model/useDepartmentTree';

interface DepartmentsTreeProps extends WithDataTestid {
  selectedDepartmentId?: DepartmentId;
  onSelect?: (departmentId: DepartmentId) => void;
  renderAction?: (node: DepartmentTreeDataNode) => React.ReactNode;
  emptyContent?: React.ReactNode;
}

const displayName = 'DepartmentsTree';
const testid = buildTestid(displayName);
const EMPTY_TREE_DATA: DepartmentTreeDataNode[] = [];

// возвращает путь до выбранного подразделения в виде массива id от корня до выбранного узла
function getDepartmentPath(
  nodes: DepartmentTreeDataNode[],
  departmentId: DepartmentId,
): DepartmentId[] {
  return nodes.reduce<DepartmentId[]>((path, node) => {
    if (path.length) {
      return path;
    }

    if (node.key === departmentId) {
      return [node.key];
    }

    const childPath = getDepartmentPath(node.children ?? [], departmentId);

    if (childPath.length) {
      return [node.key, ...childPath];
    }

    return [];
  }, []);
}

// возвращает иконку для узлов дерева, у которых есть дети
// и null для узлов первого и пятого уровня
function DepartmentsTreeSwitcherIcon(
  { data, ...props }: TreeNodeProps,
): React.ReactNode {
  const departmentNode = data as DepartmentTreeDataNode | undefined;

  if (departmentNode?.level === 1 || departmentNode?.level === MAX_DEPARTMENT_LEVEL) {
    return null;
  }

  return (
    <TreeSwitcherIcon
      data={departmentNode}
      {...props}
    />
  );
}

function DepartmentsTree({
  selectedDepartmentId = undefined,
  onSelect = undefined,
  renderAction = undefined,
  emptyContent = undefined,
  'data-testid': dataTestId = undefined,
}: DepartmentsTreeProps): React.JSX.Element {
  const getDepartmentTree = useDepartmentTree();
  const treeData = getDepartmentTree.data ?? EMPTY_TREE_DATA;
  const firstLevelDepartmentIds = useMemo(() => treeData.map(({ key }) => key), [treeData]);
  const selectedDepartmentPath = useMemo(() => (
    selectedDepartmentId ? getDepartmentPath(treeData, selectedDepartmentId) : []
  ), [selectedDepartmentId, treeData]);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [isExpandedKeysInitialized, setIsExpandedKeysInitialized] = useState(false);

  // оборачивает название подразделения и рендерит кнопку действия, если она есть
  // кнопка действия не рендерится для узлов пятого уровня
  const renderTitle = useCallback((node: DepartmentTreeDataNode) => (
    <span className={st.Title}>
      <span className={st.Name}>{node.name}</span>
      {node.level < MAX_DEPARTMENT_LEVEL && renderAction ? (
        // не даем rc-tree обработать клик по кнопке как клик по строке
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <span
          className={st.Action}
          onClick={(event) => event.stopPropagation()}
        >
          {renderAction(node)}
        </span>
      ) : null}
    </span>
  ), [renderAction]);

  // раскрываем первый уровень при первой загрузке, а при переходе к подразделению - путь до него
  useEffect(() => {
    if (!firstLevelDepartmentIds.length) {
      return;
    }

    if (!isExpandedKeysInitialized) {
      setExpandedKeys(selectedDepartmentPath.length ? selectedDepartmentPath : firstLevelDepartmentIds);
      setIsExpandedKeysInitialized(true);

      return;
    }

    if (selectedDepartmentPath.length) {
      setExpandedKeys((keys) => [
        ...keys,
        ...selectedDepartmentPath.filter((key) => !keys.includes(key)),
      ]);
    }
  }, [firstLevelDepartmentIds, isExpandedKeysInitialized, selectedDepartmentPath]);

  if (getDepartmentTree.isLoading) {
    return (
      <div className={st.Root}>
        <div className={st.Loader}>
          <Loader />
        </div>
      </div>
    );
  }

  if (getDepartmentTree.isSuccess && !treeData.length) {
    return (
      <div className={st.Root}>
        {emptyContent}
      </div>
    );
  }

  return (
    <div className={st.Root}>
      <Tree<DepartmentTreeDataNode>
        expandedKeys={expandedKeys}
        onExpand={(keys) => setExpandedKeys(keys)}
        onSelect={(keys, info) => {
          const departmentId = keys[0] ?? info.node.key;

          if (typeof departmentId === 'string') {
            onSelect?.(departmentId);
          }
        }}
        selectedKeys={selectedDepartmentId ? [selectedDepartmentId] : []}
        switcherIcon={DepartmentsTreeSwitcherIcon}
        titleRender={renderTitle}
        treeData={treeData}
        {...testid(dataTestId)}
      />
    </div>
  );
}

DepartmentsTree.displayName = displayName;

export { DepartmentsTree };
export type { DepartmentsTreeProps };
